import React, { useState, useEffect } from 'react';
import API from '../services/api';

const S = {
  page: {
    margin: '2rem auto', maxWidth: 1280, padding: '0 1.5rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: 'var(--text-strong)',
  },
  h1: { fontSize: '1.6rem', fontWeight: 600, margin: '0 0 0.25rem', letterSpacing: '-0.02em' },
  subtle: { color: 'var(--text-muted)', fontSize: '0.9rem' },
  card: {
    background: '#fff', border: '1px solid var(--border-color)', borderRadius: 10,
    padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  cardTitle: {
    fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'var(--text-strong)', margin: '0 0 0.9rem',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.78rem', fontWeight: 500, color: 'var(--text-strong)' },
  input: {
    padding: '0.5rem 0.65rem', border: '1px solid var(--border-color)', borderRadius: 6,
    fontSize: '0.9rem', background: '#fff', fontFamily: 'inherit',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  btn: {
    padding: '0.5rem 0.95rem', border: '1px solid var(--border-color)', borderRadius: 6,
    background: '#fff', color: 'var(--text-strong)', fontSize: '0.875rem', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnPrimary: { background: 'var(--text-strong)', color: '#fff', borderColor: 'var(--text-strong)' },
  btnGhost: {
    background: 'transparent', border: '1px solid transparent', color: 'var(--text-strong)',
    fontSize: '0.82rem',
  },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.88rem' },
  th: {
    textAlign: 'left', padding: '0.55rem 0.75rem', fontWeight: 600, color: 'var(--text-strong)',
    borderBottom: '2px solid var(--border-color)', fontSize: '0.78rem', textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: { padding: '0.55rem 0.75rem', borderBottom: '1px solid var(--surface-muted)' },
  tdNum: { padding: '0.55rem 0.75rem', borderBottom: '1px solid var(--surface-muted)', textAlign: 'right' },
  totalsRow: { fontWeight: 600, background: 'var(--surface-muted)' },
};

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function AdminRetirementPage() {
  const [therapists, setTherapists] = useState([]);
  const [settings, setSettings] = useState(null);

  // Settings card state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('Brett.trusty@edwardjones.com');
  const [employerPct, setEmployerPct] = useState('2');
  const [participantMap, setParticipantMap] = useState({});
  const [manualEntries, setManualEntries] = useState([
    { name: 'Cami Baker', ej_account: '', deferral_pct: '', salary: '', pretax_deductions: '' },
    { name: 'Eric Baker', ej_account: '', deferral_pct: '', salary: '', pretax_deductions: '' },
  ]);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userObj = JSON.parse(localStorage.getItem('user') || '{}');
    if (!token || !['OFFICE_ADMIN', 'PORTAL_ADMIN'].includes(userObj.role)) {
      window.location.href = '/';
      return;
    }
    (async () => {
      try {
        const res = await API.get('/admin/retirement/settings');
        const s = res.data.settings;
        setTherapists(res.data.therapists);
        setSettings(s);
        setRecipientEmail(s.recipient_email || 'Brett.trusty@edwardjones.com');
        setEmployerPct(String(s.employer_nonelective_pct ?? 2));
        const pm = {};
        (s.participants || []).forEach((p) => {
          pm[String(p.therapist_id)] = {
            ej_account: p.ej_account || '',
            deferral_pct: String(p.deferral_pct ?? ''),
            pretax_deductions: p.pretax_deductions ? String(p.pretax_deductions) : '',
          };
        });
        setParticipantMap(pm);
        if (s.manual_entries && s.manual_entries.length) {
          setManualEntries(s.manual_entries.map((e) => ({
            name: e.name,
            ej_account: e.ej_account || '',
            deferral_pct: String(e.deferral_pct ?? ''),
            salary: e.salary ? String(e.salary) : '',
            pretax_deductions: e.pretax_deductions ? String(e.pretax_deductions) : '',
          })));
        }
        const incomplete = !s.recipient_email || res.data.therapists.some((t) => {
          const p = pm[String(t.id)];
          return !p || !p.ej_account;
        });
        setSettingsOpen(incomplete);
      } catch (e) {
        console.error(e);
        alert('Error loading retirement settings.');
      }
    })();
  }, []);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      const participants = therapists.map((t) => ({
        therapist_id: t.id,
        ej_account: (participantMap[String(t.id)]?.ej_account || '').trim(),
        deferral_pct: Number(participantMap[String(t.id)]?.deferral_pct) || 0,
        pretax_deductions: Number(participantMap[String(t.id)]?.pretax_deductions) || 0,
      }));
      const me = manualEntries.map((e) => ({
        name: e.name,
        ej_account: (e.ej_account || '').trim(),
        deferral_pct: Number(e.deferral_pct) || 0,
        salary: Number(e.salary) || 0,
        pretax_deductions: Number(e.pretax_deductions) || 0,
      }));
      const res = await API.put('/admin/retirement/settings', {
        recipientEmail,
        employerNonelectivePct: Number(employerPct) || 2,
        participants,
        manualEntries: me,
      });
      setSettings(res.data.settings);
      alert('Settings saved.');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error saving settings.');
    } finally {
      setSavingSettings(false);
    }
  }

  function updateParticipant(therapistId, field, value) {
    setParticipantMap((m) => ({
      ...m,
      [String(therapistId)]: { ...(m[String(therapistId)] || {}), [field]: value },
    }));
  }

  function updateManualEntry(idx, field, value) {
    setManualEntries((entries) =>
      entries.map((e, i) => (i === idx ? { ...e, [field]: value } : e))
    );
  }

  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [checkedTherapists, setCheckedTherapists] = useState({});
  const [preview, setPreview] = useState(null);
  const [editableRows, setEditableRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [sendTo, setSendTo] = useState('Brett.trusty@edwardjones.com');
  const [sendMessage, setSendMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/admin/retirement/pay-periods');
        setPayPeriods(res.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    const checked = {};
    therapists.forEach((t) => { checked[String(t.id)] = true; });
    setCheckedTherapists(checked);
  }, [therapists]);

  useEffect(() => {
    if (settings) setSendTo(settings.recipient_email || 'Brett.trusty@edwardjones.com');
  }, [settings]);

  function formatPeriod(row) {
    const s = new Date(row.start_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(row.end_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} – ${e}`;
  }

  function buildPayload() {
    const period = payPeriods.find((p) => `${p.start_date}|${p.end_date}` === selectedPeriod);
    if (!period) return null;
    const therapistIds = therapists
      .filter((t) => checkedTherapists[String(t.id)])
      .map((t) => t.id);
    const me = manualEntries.map((e) => ({
      name: e.name,
      ej_account: e.ej_account || '',
      deferral_pct: Number(e.deferral_pct) || 0,
      salary: Number(e.salary) || 0,
      pretax_deductions: Number(e.pretax_deductions) || 0,
    }));
    return {
      payPeriodStart: period.start_date,
      payPeriodEnd: period.end_date,
      therapistIds,
      manualEntries: me,
    };
  }

  function seedEditable(rows) {
    return (rows || []).map((r) => ({
      ...r,
      salary_deferral: String(r.salary_deferral ?? ''),
      employer_nonelective: String(r.employer_nonelective ?? ''),
    }));
  }

  function updateEditableRow(idx, field, value) {
    setEditableRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }

  const editedGrandTotal = editableRows.reduce(
    (s, r) => s + (Number(r.salary_deferral) || 0) + (Number(r.employer_nonelective) || 0),
    0
  );

  async function loadPreview() {
    if (!selectedPeriod) { alert('Select a pay period first.'); return; }
    setBusy(true);
    setPreview(null);
    setEditableRows([]);
    setLastSubmission(null);
    const payload = buildPayload();
    if (!payload) { setBusy(false); return; }
    try {
      const res = await API.post('/admin/retirement/preview', payload);
      setPreview(res.data);
      setEditableRows(seedEditable(res.data.rows));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error loading preview.');
    } finally {
      setBusy(false);
    }
  }

  async function generatePdf() {
    if (!selectedPeriod) return;
    setBusy(true);
    const payload = buildPayload();
    if (!payload) { setBusy(false); return; }
    payload.rows = editableRows.map((r) => ({
      name: r.name,
      ej_account: r.ej_account,
      salary_deferral: Number(r.salary_deferral) || 0,
      employer_nonelective: Number(r.employer_nonelective) || 0,
    }));
    try {
      const res = await API.post('/admin/retirement/generate', payload);
      setLastSubmission(res.data);
      setPreview({ rows: res.data.rows, grandTotal: res.data.total_amount });
      setEditableRows(seedEditable(res.data.rows));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error generating PDF.');
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf(submissionId) {
    try {
      const res = await API.get(`/admin/retirement/submissions/${submissionId}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `retirement-submission-${submissionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      alert('Error downloading PDF.');
    }
  }

  async function sendPdf() {
    if (sending || !lastSubmission?.submission_id || !sendTo) return;
    setSending(true);
    try {
      await API.post(`/admin/retirement/submissions/${lastSubmission.submission_id}/send`, {
        sentTo: sendTo,
        message: sendMessage,
      });
      alert('Email sent.');
      loadHistory();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Error sending email.');
    } finally {
      setSending(false);
    }
  }

  async function loadHistory() {
    try {
      const res = await API.get('/admin/retirement/submissions');
      setHistory(res.data);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    if (historyOpen) loadHistory();
  }, [historyOpen]);

  const canPreview = !!selectedPeriod && !busy;
  const canGenerate = !!preview && !busy;
  const canSend = !!lastSubmission && !!sendTo && !sending;

  return (
    <div style={S.page}>
      <h1 style={S.h1}>SIMPLE IRA Retirement</h1>
      <p style={S.subtle}>
        Generate and email the Edward Jones SIMPLE IRA Contribution Transmittal Form for each pay period.
      </p>

      {/* Settings card */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>
          Settings{' '}
          <button style={{ ...S.btn, ...S.btnGhost, padding: '0.1rem 0.5rem' }}
            onClick={() => setSettingsOpen((o) => !o)}>
            {settingsOpen ? 'Hide' : 'Edit'}
          </button>
        </h2>
        {settingsOpen && (
          <>
            <div style={{ ...S.grid2, marginBottom: '1rem' }}>
              <div style={S.field}>
                <label style={S.label}>Recipient email (Edward Jones advisor)</label>
                <input style={S.input} value={recipientEmail}
                  placeholder="advisor@edwardjones.com"
                  onChange={(e) => setRecipientEmail(e.target.value)} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Employer non-elective contribution %</label>
                <input style={S.input} type="number" min="0" max="100" step="0.1"
                  value={employerPct}
                  onChange={(e) => setEmployerPct(e.target.value)} />
              </div>
            </div>

            {therapists.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ ...S.label, marginBottom: '0.5rem' }}>Therapist EJ accounts and deferral %</p>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Name</th>
                      <th style={S.th}>EJ Account #</th>
                      <th style={S.th}>Deferral %</th>
                      <th style={S.th}>Salary</th>
                      <th style={S.th}>Pre-tax Deduct.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {therapists.map((t) => (
                      <tr key={t.id}>
                        <td style={S.td}>{t.first_name} {t.last_name}</td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '120px' }}
                            value={participantMap[String(t.id)]?.ej_account || ''}
                            placeholder="Account #"
                            onChange={(e) => updateParticipant(t.id, 'ej_account', e.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '70px' }}
                            type="number" min="0" max="100" step="0.5"
                            value={participantMap[String(t.id)]?.deferral_pct || ''}
                            placeholder="%"
                            onChange={(e) => updateParticipant(t.id, 'deferral_pct', e.target.value)} />
                        </td>
                        <td style={{ ...S.td, color: 'var(--text-muted)' }} title="Therapist base comes from payroll">—</td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '110px' }}
                            type="number" min="0" step="0.01"
                            value={participantMap[String(t.id)]?.pretax_deductions || ''}
                            placeholder="0.00"
                            onChange={(e) => updateParticipant(t.id, 'pretax_deductions', e.target.value)} />
                        </td>
                      </tr>
                    ))}
                    {manualEntries.map((e, i) => (
                      <tr key={e.name}>
                        <td style={S.td}>{e.name}</td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '120px' }}
                            value={e.ej_account}
                            placeholder="Account #"
                            onChange={(ev) => updateManualEntry(i, 'ej_account', ev.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '70px' }}
                            type="number" min="0" max="100" step="0.5"
                            value={e.deferral_pct}
                            placeholder="%"
                            onChange={(ev) => updateManualEntry(i, 'deferral_pct', ev.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '120px' }}
                            type="number" min="0" step="0.01"
                            value={e.salary}
                            placeholder="0.00"
                            onChange={(ev) => updateManualEntry(i, 'salary', ev.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.input, width: '110px' }}
                            type="number" min="0" step="0.01"
                            value={e.pretax_deductions}
                            placeholder="0.00"
                            onChange={(ev) => updateManualEntry(i, 'pretax_deductions', ev.target.value)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              style={{ ...S.btn, ...S.btnPrimary, ...(savingSettings ? S.btnDisabled : {}) }}
              disabled={savingSettings} onClick={saveSettings}>
              {savingSettings ? 'Saving…' : 'Save settings'}
            </button>
          </>
        )}
      </div>

      {/* Filter card */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Pay Period</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'end' }}>
          <div style={S.field}>
            <label style={S.label}>Pay period</label>
            <select style={S.input} value={selectedPeriod}
              onChange={(e) => { setSelectedPeriod(e.target.value); setPreview(null); setLastSubmission(null); }}>
              <option value="">— Select a pay period —</option>
              {payPeriods.map((p) => (
                <option key={`${p.start_date}|${p.end_date}`} value={`${p.start_date}|${p.end_date}`}>
                  {formatPeriod(p)}
                </option>
              ))}
            </select>
          </div>
          <button style={{ ...S.btn, ...(canPreview ? {} : S.btnDisabled) }}
            disabled={!canPreview} onClick={loadPreview}>
            {busy ? 'Loading…' : 'Preview'}
          </button>
        </div>

        {selectedPeriod && therapists.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ ...S.label, marginBottom: '0.5rem' }}>Include therapists</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {therapists.map((t) => (
                <label key={t.id} style={{ display: 'inline-flex', gap: '0.3rem', fontSize: '0.88rem', alignItems: 'center' }}>
                  <input type="checkbox"
                    checked={!!checkedTherapists[String(t.id)]}
                    onChange={(e) => setCheckedTherapists((c) => ({ ...c, [String(t.id)]: e.target.checked }))} />
                  {t.first_name} {t.last_name}
                </label>
              ))}
            </div>
          </div>
        )}

        {selectedPeriod && manualEntries.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ ...S.label, marginBottom: '0.5rem' }}>Manual salary entries</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {manualEntries.map((e, i) => (
                <div key={e.name} style={S.field}>
                  <label style={S.label}>{e.name} salary</label>
                  <input style={{ ...S.input, width: '140px' }}
                    type="number" min="0" step="0.01"
                    value={e.salary || ''}
                    placeholder="0.00"
                    onChange={(ev) => updateManualEntry(i, 'salary', ev.target.value)} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Preview table */}
      {preview && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>Contribution Preview</h2>
          <p style={{ ...S.subtle, marginTop: '-0.5rem', marginBottom: '0.75rem' }}>
            Salary Deferral and Employer amounts are editable — adjust any row to match ADP before generating. Totals update automatically.
          </p>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Name</th>
                <th style={S.th}>EJ Account</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Pay</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Pre-tax</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Taxable</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Salary Deferral</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Employer Non-Elective</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {editableRows.map((row, i) => {
                const rowTotal = (Number(row.salary_deferral) || 0) + (Number(row.employer_nonelective) || 0);
                return (
                  <tr key={i}>
                    <td style={S.td}>{row.name}</td>
                    <td style={S.td}>{row.ej_account || '—'}</td>
                    <td style={S.tdNum}>{fmtMoney(row.base)}</td>
                    <td style={S.tdNum}>{row.pretax_deductions ? `−${fmtMoney(row.pretax_deductions)}` : '—'}</td>
                    <td style={S.tdNum}>{fmtMoney(row.taxable_base ?? row.base)}</td>
                    <td style={S.tdNum}>
                      <input
                        style={{ ...S.input, width: '90px', textAlign: 'right' }}
                        type="number" min="0" step="0.01"
                        value={row.salary_deferral}
                        onChange={(e) => updateEditableRow(i, 'salary_deferral', e.target.value)} />
                    </td>
                    <td style={S.tdNum}>
                      <input
                        style={{ ...S.input, width: '90px', textAlign: 'right' }}
                        type="number" min="0" step="0.01"
                        value={row.employer_nonelective}
                        onChange={(e) => updateEditableRow(i, 'employer_nonelective', e.target.value)} />
                    </td>
                    <td style={S.tdNum}>{fmtMoney(rowTotal)}</td>
                  </tr>
                );
              })}
              <tr style={S.totalsRow}>
                <td style={S.td} colSpan={7}>Total</td>
                <td style={S.tdNum}>{fmtMoney(editedGrandTotal)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              style={{ ...S.btn, ...S.btnPrimary, ...(canGenerate ? {} : S.btnDisabled) }}
              disabled={!canGenerate} onClick={generatePdf}>
              {busy ? 'Generating…' : lastSubmission ? 'Re-generate PDF' : 'Generate PDF'}
            </button>
            {lastSubmission && (
              <button
                style={{ ...S.btn }}
                onClick={() => downloadPdf(lastSubmission.submission_id)}>
                Download PDF
              </button>
            )}
          </div>

          {lastSubmission && (
            <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
              <p style={S.cardTitle}>Send to Edward Jones</p>
              <div style={{ ...S.grid2, marginBottom: '0.75rem' }}>
                <div style={S.field}>
                  <label style={S.label}>To</label>
                  <input style={S.input} value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
                </div>
              </div>
              <div style={{ ...S.field, marginBottom: '0.75rem' }}>
                <label style={S.label}>Message (optional)</label>
                <textarea style={{ ...S.input, minHeight: '80px', resize: 'vertical' }}
                  value={sendMessage} onChange={(e) => setSendMessage(e.target.value)} />
              </div>
              <button
                style={{ ...S.btn, ...S.btnPrimary, ...(canSend ? {} : S.btnDisabled) }}
                disabled={!canSend} onClick={sendPdf}>
                {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* History section */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>
          Submission History{' '}
          <button style={{ ...S.btn, ...S.btnGhost, padding: '0.1rem 0.5rem' }}
            onClick={() => setHistoryOpen((o) => !o)}>
            {historyOpen ? 'Hide' : 'Show'}
          </button>
        </h2>
        {historyOpen && (
          history.length === 0
            ? <p style={S.subtle}>No submissions yet.</p>
            : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Date</th>
                    <th style={S.th}>Pay Period</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                    <th style={S.th}>Sent To</th>
                    <th style={S.th}>Sent At</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td style={S.td}>{new Date(h.created_at).toLocaleDateString()}</td>
                      <td style={S.td}>{formatPeriod({ start_date: h.pay_period_start, end_date: h.pay_period_end })}</td>
                      <td style={S.tdNum}>{fmtMoney(h.total_amount)}</td>
                      <td style={S.td}>{h.sent_to || '—'}</td>
                      <td style={S.td}>{h.sent_at ? new Date(h.sent_at).toLocaleDateString() : '—'}</td>
                      <td style={S.td}>
                        <button
                          style={{ ...S.btn, fontSize: '0.8rem', padding: '0.25rem 0.6rem' }}
                          onClick={() => downloadPdf(h.id)}>
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        )}
      </div>
    </div>
  );
}

export default AdminRetirementPage;
